import { useState } from 'react';
import { ForecasterLogo } from '../components/ForecasterLogo';

/* ─── Navigation chrome (dots + counter + arrows + keyboard) ─── */
function SlideChrome({ current, total, onPrev, onNext }: { current: number; total: number; onPrev: () => void; onNext: () => void }) {
  return (
    <>
      {current < total - 1 && (
        <button onClick={onNext} className="fixed right-6 top-1/2 -translate-y-1/2 z-50 text-gray-600 hover:text-white transition p-2">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      )}
      {current > 0 && (
        <button onClick={onPrev} className="fixed left-6 top-1/2 -translate-y-1/2 z-50 text-gray-600 hover:text-white transition p-2">
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
      )}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`rounded-full transition-all ${i === current ? 'w-6 h-2 bg-emerald-500' : 'w-2 h-2 bg-gray-700'}`} />
        ))}
      </div>
      <div className="fixed bottom-6 right-6 z-50 text-gray-600 text-sm font-mono">
        {String(current + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </div>
    </>
  );
}

function Slide({ children }: { children: React.ReactNode }) {
  return (
    <section className="min-h-screen w-full flex flex-col items-center justify-center px-8 md:px-16 lg:px-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(16,185,129,0.04)_0%,_transparent_70%)]" />
      <div className="relative z-10 max-w-4xl w-full">{children}</div>
    </section>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-emerald-500 text-sm font-mono uppercase tracking-[0.2em] mb-8">{children}</p>;
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 border border-emerald-500/30 text-emerald-400 px-4 py-1.5 rounded-full text-sm">
      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
      {children}
    </span>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 1: THE HOOK
   ════════════════════════════════════════════════════════ */
function TitleSlide() {
  return (
    <Slide>
      <div className="text-center flex flex-col items-center">
        <div className="mb-8">
          <ForecasterLogo size="default" clickable={false} className="h-10" />
        </div>
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-2">
          <span className="text-white">Make Every GPU Dollar</span>
          <br />
          <span className="text-emerald-400">Work 3x Harder</span>
        </h1>
        <div className="mt-6 mb-6">
          <Pill>The Intelligence Layer for Distributed GPU Inference</Pill>
        </div>
        <p className="text-gray-400 text-lg max-w-2xl">
          Your GPUs are expensive. Your serving stack is blind.
          Forecaster sees what&rsquo;s coming and acts before your users notice.
        </p>
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 2: THE PROBLEM ($700B and growing)
   ════════════════════════════════════════════════════════ */
function ProblemSlide() {
  return (
    <Slide>
      <SectionLabel>The Problem</SectionLabel>
      <h2 className="text-5xl md:text-7xl font-bold text-red-400 mb-2">$700B</h2>
      <p className="text-gray-300 text-xl mb-4">spent on AI infrastructure in 2026. Most of it wasted.</p>
      <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-3xl">
        Hyperscalers are spending nearly $2 billion per day on GPU infrastructure.
        60 to 70% of that goes to inference. Yet the average GPU cluster runs at
        30 to 40% utilization. The hardware is not the bottleneck. The software managing
        it is blind, reactive, and treats every GPU the same.
      </p>
      <div className="space-y-3 mb-10">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-white font-medium">GPU compute capacity</span>
            <span className="text-gray-500">80x growth in a decade</span>
          </div>
          <div className="h-3 bg-white/[0.05] rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500/60 rounded-full" style={{ width: '90%' }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-white font-medium">Memory bandwidth</span>
            <span className="text-red-400">Only 17x growth</span>
          </div>
          <div className="h-3 bg-white/[0.05] rounded-full overflow-hidden">
            <div className="h-full bg-red-500/60 rounded-full" style={{ width: '35%' }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-white font-medium">Inference serving intelligence</span>
            <span className="text-red-400">Near zero</span>
          </div>
          <div className="h-3 bg-white/[0.05] rounded-full overflow-hidden">
            <div className="h-full bg-red-500/60 rounded-full" style={{ width: '5%' }} />
          </div>
        </div>
      </div>
      <p className="text-gray-500 text-sm italic">
        Google Research (Ma &amp; Patterson, Jan 2026): &ldquo;The current AI hardware philosophy is a mismatch to LLM decode inference.&rdquo;
      </p>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 3: WHY NOW (the agentic + quantum multiplier)
   ════════════════════════════════════════════════════════ */
function WhyNowSlide() {
  return (
    <Slide>
      <SectionLabel>Why Now</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
        Inference demand is about to<br />
        <span className="text-emerald-400">multiply 10 to 100x.</span>
      </h2>
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-start gap-4 border border-white/[0.06] rounded-xl p-5">
          <span className="text-3xl">1x</span>
          <div>
            <p className="text-white font-semibold">Chatbot era (2023)</p>
            <p className="text-gray-400 text-sm">1 user message = 1 inference call</p>
          </div>
        </div>
        <div className="flex items-start gap-4 border border-emerald-500/20 bg-emerald-500/[0.03] rounded-xl p-5">
          <span className="text-3xl text-emerald-400">10-50x</span>
          <div>
            <p className="text-white font-semibold">Agentic era (now)</p>
            <p className="text-gray-400 text-sm">1 user action = 10 to 50 chained inference calls. Coding agents, research agents, workflow automation.</p>
          </div>
        </div>
        <div className="flex items-start gap-4 border border-emerald-500/20 bg-emerald-500/[0.03] rounded-xl p-5">
          <span className="text-3xl text-emerald-400">+quantum</span>
          <div>
            <p className="text-white font-semibold">Every quantum computer needs GPU inference</p>
            <p className="text-gray-400 text-sm">NVIDIA Ising: AI models running on GPUs for quantum calibration and error correction. Quantum expands the inference market.</p>
          </div>
        </div>
      </div>
      <p className="text-gray-500 text-sm mt-8">
        The serving layer that worked for chatbots collapses under agentic workloads. Reactive scaling cannot keep up.
      </p>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 4: THE GAP (what's missing)
   ════════════════════════════════════════════════════════ */
function GapSlide() {
  return (
    <Slide>
      <SectionLabel>The Gap</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
        The serving stack has no brain.
      </h2>
      <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-3xl">
        NVIDIA builds the best GPUs. vLLM, SGLang, and TensorRT-LLM are excellent serving frameworks.
        Kubernetes orchestrates containers. But between the model and the metal, there is no intelligence layer
        that predicts demand, routes across heterogeneous silicon, manages memory across tiers, or optimizes cost in real time.
      </p>
      <div className="space-y-2 max-w-2xl font-mono text-sm">
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 w-32">Models</span>
          <div className="flex-1 h-8 bg-emerald-500/10 border border-emerald-500/20 rounded flex items-center px-3 text-gray-300">GPT-4, Llama, DeepSeek, Mixtral, Gemini</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-red-400 w-32 font-bold">??? Gap</span>
          <div className="flex-1 h-8 bg-red-500/10 border border-red-500/30 rounded flex items-center px-3 text-red-300 font-bold">No intelligence. Reactive. Blind.</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 w-32">Serving</span>
          <div className="flex-1 h-8 bg-emerald-500/10 border border-emerald-500/20 rounded flex items-center px-3 text-gray-300">vLLM, SGLang, TensorRT-LLM, Dynamo</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 w-32">Orchestration</span>
          <div className="flex-1 h-8 bg-emerald-500/10 border border-emerald-500/20 rounded flex items-center px-3 text-gray-300">Kubernetes, Kueue, Karpenter</div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 w-32">Hardware</span>
          <div className="flex-1 h-8 bg-emerald-500/10 border border-emerald-500/20 rounded flex items-center px-3 text-gray-300">H100, B200, MI300X, TPU, Gaudi, Inferentia</div>
        </div>
      </div>
      <p className="text-emerald-400 font-semibold text-lg mt-10">Forecaster fills that gap.</p>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 5: WHAT FORECASTER DOES (business outcomes)
   ════════════════════════════════════════════════════════ */
function SolutionSlide() {
  return (
    <Slide>
      <SectionLabel>Solution</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-4">
        The intelligence layer<br />
        <span className="text-emerald-400">between your models and your GPUs.</span>
      </h2>
      <p className="text-gray-400 text-lg mb-10 max-w-3xl">
        Forecaster sits on top of your existing stack. It does not replace vLLM, Kubernetes, or your GPUs.
        It makes all of them smarter. One Helm install. Self-hosted. Your data never leaves your cluster.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            title: 'Predict, don\u2019t react',
            desc: 'Forecaster predicts demand 15 minutes ahead and scales before your users notice. Eliminates cold-start latency spikes entirely.',
          },
          {
            title: 'Route to the right GPU',
            desc: 'Intelligent routing across NVIDIA, AMD, TPU, and custom silicon. Matches workloads to hardware by cost, latency, and memory hierarchy.',
          },
          {
            title: 'Optimize the memory hierarchy',
            desc: 'Intelligent memory management that puts data where it needs to be, when it needs to be there. No more OOM kills or wasted capacity.',
          },
          {
            title: 'See every dollar',
            desc: 'Per-request cost attribution. Know exactly what each inference call costs, across which GPU, in which cloud, at what utilization.',
          },
        ].map((p) => (
          <div key={p.title} className="border border-white/[0.06] rounded-xl p-5">
            <p className="text-white font-semibold mb-2">{p.title}</p>
            <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 6: OUTCOMES (the numbers that matter)
   ════════════════════════════════════════════════════════ */
function OutcomesSlide() {
  return (
    <Slide>
      <SectionLabel>Outcomes</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-12">
        What your infrastructure team gets.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        {[
          { value: '3x', label: 'More throughput from the same hardware' },
          { value: 'Zero', label: 'Cold-start latency spikes' },
          { value: '15 min', label: 'Ahead-of-demand prediction' },
        ].map((m) => (
          <div key={m.label} className="text-center border border-emerald-500/15 bg-emerald-500/[0.03] rounded-2xl p-8">
            <p className="text-4xl md:text-5xl font-bold text-emerald-400 mb-3">{m.value}</p>
            <p className="text-gray-300 text-sm font-medium">{m.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-white/[0.06] rounded-xl p-5">
          <p className="text-white font-semibold mb-1">Works with your existing stack</p>
          <p className="text-gray-400 text-sm">vLLM, SGLang, TensorRT-LLM, Triton, Ray Serve. Forecaster integrates with your existing stack.</p>
        </div>
        <div className="border border-white/[0.06] rounded-xl p-5">
          <p className="text-white font-semibold mb-1">Any accelerator, any cloud</p>
          <p className="text-gray-400 text-sm">NVIDIA H100/B200, AMD MI300X, Google TPU, AWS Inferentia, Intel Gaudi. Multi-cloud overflow built in.</p>
        </div>
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 7: COMPETITIVE MOAT (why not Dynamo, why not DIY)
   ════════════════════════════════════════════════════════ */
function MoatSlide() {
  return (
    <Slide>
      <SectionLabel>Differentiation</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
        Why us. Why now.
      </h2>
      <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-3xl">
        Serving frameworks optimize one GPU. Orchestrators schedule containers.
        Nobody optimizes the intelligence layer in between.
      </p>
      <div className="space-y-6 max-w-2xl">
        <div className="border border-white/[0.06] rounded-xl p-6">
          <p className="text-white font-semibold text-lg mb-2">Predictive, not reactive</p>
          <p className="text-gray-400 text-sm leading-relaxed">
            Existing infrastructure reacts after problems occur. Forecaster sees demand
            before it arrives and prepares your fleet in advance. The result: your users
            never experience a cold start.
          </p>
        </div>
        <div className="border border-white/[0.06] rounded-xl p-6">
          <p className="text-white font-semibold text-lg mb-2">Vendor-agnostic by design</p>
          <p className="text-gray-400 text-sm leading-relaxed">
            Works across GPU vendors and serving frameworks. Your team picks the best
            hardware for each workload. Forecaster makes that choice intelligent and automatic.
          </p>
        </div>
        <div className="border border-white/[0.06] rounded-xl p-6">
          <p className="text-white font-semibold text-lg mb-2">Complements your stack</p>
          <p className="text-gray-400 text-sm leading-relaxed">
            Forecaster does not replace anything. It makes your existing GPUs, frameworks,
            and orchestrators work harder. Better utilization means better ROI on every
            hardware dollar already spent.
          </p>
        </div>
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 8: IP AND DEFENSIBILITY
   ════════════════════════════════════════════════════════ */
function IPSlide() {
  return (
    <Slide>
      <SectionLabel>Defensibility</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
        Deep IP. Not a wrapper.
      </h2>
      <p className="text-gray-400 text-lg leading-relaxed mb-10 max-w-3xl">
        Forecaster is not a monitoring dashboard or an API gateway. It is a novel prediction
        and optimization engine with foundational intellectual property covering the core algorithms.
      </p>
      <div className="space-y-2 max-w-2xl">
        {[
          'Proprietary prediction engine that learns from your workload patterns',
          'Novel memory optimization algorithms across the full hardware hierarchy',
          'Intelligent routing that improves with every request served',
          'Differentiated approaches to cost optimization and capacity planning',
        ].map((item) => (
          <div key={item} className="flex items-center gap-3">
            <span className="w-2 h-2 bg-emerald-500 rounded-full shrink-0" />
            <p className="text-gray-300 text-sm">{item}</p>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 9: MARKET OPPORTUNITY
   ════════════════════════════════════════════════════════ */
function MarketSlide() {
  return (
    <Slide>
      <SectionLabel>Market</SectionLabel>
      <h2 className="text-3xl md:text-5xl font-bold text-white mb-10">
        The numbers behind the opportunity.
      </h2>
      <div className="space-y-5 max-w-2xl">
        {[
          { stat: '$700B', desc: 'Hyperscaler AI infrastructure capex in 2026', source: 'Goldman Sachs, Futurum' },
          { stat: '60-70%', desc: 'Of all AI compute is now inference, up from 40% in 2024', source: 'Industry consensus' },
          { stat: '10-100x', desc: 'Inference multiplier from agentic AI workloads', source: 'GTC 2026, Jensen Huang' },
          { stat: '30-40%', desc: 'Average GPU utilization across production clusters', source: 'Industry surveys' },
          { stat: '$0', desc: 'Intelligence layers optimizing across compute, memory, AND network', source: 'Our wedge' },
        ].map((m) => (
          <div key={m.stat} className="flex items-start gap-5 border-b border-emerald-500/10 pb-4">
            <span className="text-3xl font-bold text-emerald-400 w-28 shrink-0">{m.stat}</span>
            <div>
              <p className="text-white">{m.desc}</p>
              <p className="text-gray-600 text-xs">{m.source}</p>
            </div>
          </div>
        ))}
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   SLIDE 11: THE ASK
   ════════════════════════════════════════════════════════ */
function AskSlide() {
  return (
    <Slide>
      <SectionLabel>Next Steps</SectionLabel>
      <h2 className="text-4xl md:text-6xl font-bold mb-4">
        <span className="text-emerald-400">Let&rsquo;s talk.</span>
      </h2>
      <p className="text-gray-400 text-lg mb-12 max-w-2xl">
        Forecaster is built and ready for customer validation.
        We are looking for infrastructure partners who want to make their GPU fleet dramatically more efficient.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-2xl">
        <div className="border border-white/[0.06] rounded-xl p-5">
          <p className="text-white font-semibold mb-2">For GPU Cloud Operators</p>
          <p className="text-gray-400 text-sm">
            Improve utilization across your fleet. Offer differentiated inference tiers to customers.
            Per-request cost visibility for your billing team.
          </p>
        </div>
        <div className="border border-white/[0.06] rounded-xl p-5">
          <p className="text-white font-semibold mb-2">For AI Infrastructure Teams</p>
          <p className="text-gray-400 text-sm">
            One Helm install. Immediate visibility into inference economics.
            Predictive scaling that eliminates cold starts.
          </p>
        </div>
      </div>

      <div className="border-t border-white/[0.06] pt-10 text-center">
        <p className="text-2xl font-bold text-white mb-6">
          The intelligence layer your GPUs are missing.
        </p>
        <a
          href="mailto:forecaster.k8s@gmail.com"
          className="inline-block bg-emerald-500 hover:bg-emerald-400 text-obsidian-950 font-bold px-8 py-3 rounded-full text-lg transition-colors"
        >
          forecaster.k8s@gmail.com
        </a>
        <p className="text-gray-500 text-sm mt-3">
          or <a href="mailto:forecasterai@protonmail.com" className="text-emerald-400/60 hover:text-emerald-400 transition-colors">forecasterai@protonmail.com</a>
        </p>
        <p className="text-gray-700 text-xs mt-10">&copy; 2026 Forecaster. All rights reserved.</p>
      </div>
    </Slide>
  );
}

/* ════════════════════════════════════════════════════════
   MAIN PITCH PAGE — 11 slides
   ════════════════════════════════════════════════════════ */
const slides = [
  TitleSlide,      // 1  Hook
  ProblemSlide,    // 2  $700B problem
  WhyNowSlide,     // 3  Agentic + quantum multiplier
  GapSlide,        // 4  The missing intelligence layer
  SolutionSlide,   // 5  What Forecaster does
  OutcomesSlide,   // 6  Business outcomes
  MoatSlide,       // 7  Why not Dynamo / DIY
  IPSlide,         // 8  Patents, papers, defensibility
  MarketSlide,     // 9  Market numbers
  AskSlide,        // 11 CTA
];

export function Pitch() {
  const [current, setCurrent] = useState(0);

  const goTo = (i: number) => {
    if (i >= 0 && i < slides.length) setCurrent(i);
  };

  const SlideComponent = slides[current];

  return (
    <div
      className="h-screen bg-obsidian-900 text-white font-sans overflow-hidden"
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goTo(current + 1);
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goTo(current - 1);
      }}
      tabIndex={0}
    >
      <SlideChrome current={current} total={slides.length} onPrev={() => goTo(current - 1)} onNext={() => goTo(current + 1)} />
      <SlideComponent />
    </div>
  );
}
